// ============================================================
// LLHlsEngine — Low-Latency HLS (M3U8 v9, 400ms parts)
// Target: 2.5–3.0s observed latency
// ============================================================
import type { HlsConfig } from 'hls.js';
import { BaseHlsEngine } from './BaseHlsEngine';
import { createLLAbrController } from './abr/llAbrController';
import { createPlaylistRetryController } from './BaseHlsEngine/playlistRetryController';
import {
  catchUpThreshold,
  createLagInstabilityDetector,
  LATENCY_TARGET_MAX_SECONDS,
} from './BaseHlsEngine/latencyController';
/** Loader hint; the measured first-frame position is aligned separately. */
const HLS_STARTUP_SYNC_HINT_SECONDS = 2.5;

export class LLHlsEngine extends BaseHlsEngine {
  readonly name = 'll-hls';
  readonly isLowLatency = true;

  // State for deferred-switch step-back retry
  private lastSwitchIdx = -1;
  private lastSwitchSeekTo = 0;
  private lastSwitchTargetDuration = 2;
  private switchRetryCount = 0;
  private static readonly MAX_SWITCH_RETRIES = 6;

  /**
   * Event-driven instability detector — called by hlsEvents.ts on each
   * LEVEL_UPDATED event. Emits error 4001 when the stream cannot hold low
   * latency for 60 consecutive seconds with no improvement.
   */
  readonly instabilityDetector = createLagInstabilityDetector({
    // Follows the adaptive target (4.0s at the base target): a target raised by
    // earlier stalls must not make the stream look permanently unstable.
    threshold: () => catchUpThreshold(this.latencyTarget.get()),
    onTriggered: () => {
      this.logger.error('LL-HLS cannot maintain low latency, suggesting fallback');
      this.eventBus.emit('error', {
        code: 4001,
        message: 'LL-HLS latency unstable, consider fallback to HLS',
        fatal: false,
      });
    },
  });

  protected override applyQualityLevel(idx: number): void {
    const hls = this.hls!;

    // Prefer the currently-polled level's details as the CDN availability proxy.
    // The target level may not be actively polled, so its details could be stale.
    const currentLevelIdx = hls.currentLevel >= 0 ? hls.currentLevel : 0;
    const referenceDetails =
      hls.levels[currentLevelIdx]?.details ?? hls.levels[idx]?.details;

    const frags = referenceDetails?.fragments ?? [];
    const targetDuration = referenceDetails?.targetduration ?? 2;

    let seekTo: number;
    if (frags.length >= 3) {
      // 3 segments before the last known end: reliably assembled on CDN, no parts
      seekTo = frags[frags.length - 3].start;
    } else if (frags.length > 0) {
      // Fewer frags in the window — start at the earliest available
      seekTo = frags[0].start;
    } else {
      // No fragment data yet — fall back to 2× targetDuration before the live sync position.
      // hls.liveSyncPosition is the hls.js-computed safe live-edge anchor; going back
      // 2 segments from there avoids CDN-unassembled parts without touching video.currentTime.
      const syncPos = hls.liveSyncPosition;
      seekTo =
        typeof syncPos === 'number' && isFinite(syncPos) && syncPos > 0
          ? Math.max(0, syncPos - targetDuration * 2)
          : 0;
    }

    this.lastSwitchIdx = idx;
    this.lastSwitchSeekTo = seekTo;
    this.lastSwitchTargetDuration = targetDuration;
    this.switchRetryCount = 0;

    this.applySwitchAtPosition(idx, seekTo);
  }

  /**
   * Direct hls.js to buffer level `idx` starting from `seekTo` without
   * touching video.currentTime. stopLoad resets the fetch pipeline;
   * startLoad(seekTo) tells hls.js which media position to request next.
   * The SourceBuffer overlap (if any) is resolved by hls.js internally.
   */
  private applySwitchAtPosition(idx: number, seekTo: number): void {
    const hls = this.hls!;
    this.logger.info(
      `applyQualityLevel: loading level ${idx} from ${seekTo.toFixed(2)}s` +
        ` (retry ${this.switchRetryCount}/${LLHlsEngine.MAX_SWITCH_RETRIES})`,
    );
    hls.stopLoad();
    hls.nextLevel = idx;
    hls.startLoad(-1);
  }

  protected override onFragLoadErrorDuringSwitch(_fragStart: number): void {
  }

  protected override getHlsConfig(): Partial<HlsConfig> {
    const errorController = createPlaylistRetryController();
    const abrController = createLLAbrController({
      logger: this.logger,
      getLatencyTarget: () => this.latencyTarget.get(),
      getQueryParams: () => this.queryParams,
      isRecoveringBuffer: () => this.isLatencyPausedForBuffer,
    });

    return {

      lowLatencyMode: true,
      ...(errorController ? { errorController } : {}),

      // Use an absolute target so startup latency does not scale with the
      // playlist segment duration (2 × 4s segments previously meant ~8s).
      liveSyncDuration: HLS_STARTUP_SYNC_HINT_SECONDS,

      // Re-sync when playback falls well outside the low-latency window.
      liveMaxLatencyDuration: 10,

      maxLiveSyncPlaybackRate: 1.0,

      liveSyncOnStallIncrease: 0,


      // ABR — selection is owned by LLAbrController (src/engine/abr). The
      // hls.js controller stays underneath it as the emergency floor: abandon
      // rules, error demotion and starvation handling are untouched.
      //
      // abrEwmaDefaultEstimate is deliberately NOT set: hls.js only bootstraps
      // its estimate from the first variant bitrate while the user config leaves
      // it undefined (level-controller onManifestLoaded). Pinning it to 500 kbps
      // forced every session to start at the lowest rung and climb from there.
      abrBandWidthFactor: 0.95,
      abrBandWidthUpFactor: 0.7,
      abrMaxWithRealBitrate: true,
      ...(abrController ? { abrController } : {}),

      // Must exceed the highest adaptive latency target, otherwise hls.js stops
      // loading before the buffer margin the target is meant to buy exists.
      maxBufferLength: LATENCY_TARGET_MAX_SECONDS + 2,
      maxMaxBufferLength: LATENCY_TARGET_MAX_SECONDS + 6,
      // LL-HLS part appends routinely leave sub-second holes between the first
      // buffered ranges. 0.1s left the playhead parked inside them until the
      // hls.js stall detector eventually nudged, which reads as a frozen first
      // frame with latency climbing; 0.5s lets hls.js skip them right away.
      maxBufferHole: 0.5,
      // hls.js raises fragGap only when the gap exceeds this tolerance (default 0.25 s).
      // 0.5 s suppresses spurious events from sub-half-second encoder gaps.
      maxFragLookUpTolerance: 0.5,

      backBufferLength: 30,

      autoStartLoad: false,

      // Fast retries for LL
      manifestLoadingMaxRetry: 10,
      manifestLoadingRetryDelay: 2000,
      manifestLoadingMaxRetryTimeout: 4000,

      levelLoadingMaxRetry: 20,
      levelLoadingRetryDelay: 1000,
      levelLoadingMaxRetryTimeout: 1000,

      startFragPrefetch: false,
      progressive: false,

      debug: false,
    };
  }

  /**
   * After FRAG_LOAD_ERROR_THRESHOLD (5) consecutive non-fatal fragLoadErrors, re-sync to the live edge.
   * This recovers the player when it has drifted behind the CDN DVR
   * window and is requesting fragments that are no longer available.
   * An explicit position is passed to startLoad because startLoad(-1) would
   * be overridden by hls.js with the stale lastCurrentTime — the very drifted
   * position we are recovering from.
   */
  protected override onFragLoadErrorThreshold(): void {
    const hls = this.hls;
    if (!hls) return;
    this.logger.warn('Repeated fragLoadErrors — player drifted off DVR window, re-syncing to live edge');
    const pos = this.getLiveResumePosition();
    hls.stopLoad();
    if (pos !== null) {
      if (this.video) {
        this.video.currentTime = pos;
      }
      hls.startLoad(pos);
    } else {
      hls.startLoad(-1);
    }
  }
}
